import type { SignArea } from '@sign/shared';

/**
 * 文档叠加合成（sign v2）：把每个签名图按其区域坐标烤进文档底图，输出成品
 * JPEG。坐标为相对底图的百分比，与排版打印实测像素一致，直接换算无需单位。
 */

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // 底图是同源签名 URL、签名是本地 dataURL —— canvas 不会被污染
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('图片加载失败'));
    img.src = src;
  });
}

export async function composeDocOverlay(args: {
  /** 文档底图（content[0].images[0]）。 */
  docSrc: string;
  areas: SignArea[];
  /** areaId → 签名 dataURL。 */
  signatures: Record<string, string>;
}): Promise<string> {
  const { docSrc, areas, signatures } = args;
  const doc = await loadImage(docSrc);
  // 底图原始像素，最长边限 2000 保证清晰又不过大
  const scale = Math.min(1, 2000 / Math.max(doc.naturalWidth, doc.naturalHeight));
  const W = Math.round(doc.naturalWidth * scale);
  const H = Math.round(doc.naturalHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d 不可用');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);
  ctx.drawImage(doc, 0, 0, W, H);

  for (const area of areas) {
    const sig = signatures[area.id];
    if (!sig) continue;
    const sigImg = await loadImage(sig);
    // 目标框（像素）
    const bx = area.xPct * W;
    const by = area.yPct * H;
    const bw = area.wPct * W;
    const bh = area.hPct * H;
    // 等比缩放签名，居中放进框内（留 8% 内边距）
    const pad = 0.08;
    const availW = bw * (1 - pad * 2);
    const availH = bh * (1 - pad * 2);
    const r = Math.min(availW / sigImg.width, availH / sigImg.height);
    const dw = sigImg.width * r;
    const dh = sigImg.height * r;
    const dx = bx + (bw - dw) / 2;
    const dy = by + (bh - dh) / 2;
    ctx.drawImage(sigImg, dx, dy, dw, dh);
  }

  return canvas.toDataURL('image/jpeg', 0.9);
}
